import { useEffect, useState } from "react";
import { PUBLIC_API_BASE_URL, fetchJsonWithRetry } from "../API/Api";

export const extractApiList = (responseData) => {
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
  return [];
};

export const getPaginationInfo = (responseData) => {
  const paginated = responseData?.data;

  if (paginated && typeof paginated === "object" && !Array.isArray(paginated)) {
    const items = Array.isArray(paginated.data) ? paginated.data : [];
    const currentPage = Number(
      paginated.current_page ?? paginated.meta?.current_page ?? 1,
    );
    const lastPage = Number(
      paginated.last_page ?? paginated.meta?.last_page ?? 1,
    );
    const perPage = Number(
      paginated.per_page ?? paginated.meta?.per_page ?? items.length,
    );
    const reportedTotal = Number(paginated.total ?? paginated.meta?.total);
    const total = Number.isFinite(reportedTotal)
      ? reportedTotal
      : lastPage > 1
        ? lastPage * (Number.isFinite(perPage) ? perPage : items.length)
        : items.length;

    return {
      items,
      currentPage: Number.isFinite(currentPage) ? currentPage : 1,
      lastPage: Number.isFinite(lastPage) ? lastPage : 1,
      total: Number.isFinite(total) ? total : items.length,
      perPage: Number.isFinite(perPage) ? perPage : items.length,
    };
  }

  const meta = responseData?.meta;
  const items = extractApiList(responseData);
  const currentPage = Number(meta?.current_page ?? 1);
  const lastPage = Number(meta?.last_page ?? 1);
  const perPage = Number(meta?.per_page ?? items.length);
  const reportedTotal = Number(meta?.total);
  const total = Number.isFinite(reportedTotal)
    ? reportedTotal
    : lastPage > 1
      ? lastPage * (Number.isFinite(perPage) ? perPage : items.length)
      : items.length;

  return {
    items,
    currentPage: Number.isFinite(currentPage) ? currentPage : 1,
    lastPage: Number.isFinite(lastPage) ? lastPage : 1,
    total: Number.isFinite(total) ? total : items.length,
    perPage: Number.isFinite(perPage) ? perPage : items.length,
  };
};

const BRANDS_PER_PAGE = 100;
const BRAND_OPTION_LIMIT = 80;

const toBrandOption = (brand) => ({
  id: brand.id,
  name: brand.name || "",
  slug: brand.slug || "",
});

const matchesBrandSearch = (item, query) => {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  return (
    String(item?.name || "")
      .toLowerCase()
      .includes(q) ||
    String(item?.slug || "")
      .toLowerCase()
      .includes(q)
  );
};

let brandsCatalogCache = null;
let inFlightBrandsCatalog = null;

export const invalidateBrandsCache = () => {
  brandsCatalogCache = null;
  inFlightBrandsCatalog = null;
};

export const fetchBrandsCatalog = async () => {
  if (brandsCatalogCache) return brandsCatalogCache;
  if (inFlightBrandsCatalog) return inFlightBrandsCatalog;

  inFlightBrandsCatalog = (async () => {
    const responseData = await fetchJsonWithRetry(
      `${PUBLIC_API_BASE_URL}/brands`,
      { headers: { Accept: "application/json" } },
    );
    brandsCatalogCache = extractApiList(responseData);
    return brandsCatalogCache;
  })().finally(() => {
    inFlightBrandsCatalog = null;
  });

  return inFlightBrandsCatalog;
};

export const fetchBrandsPage = async ({
  page = 1,
  perPage = BRANDS_PER_PAGE,
  search = "",
} = {}) => {
  const allBrands = await fetchBrandsCatalog();
  const filtered = allBrands.filter((item) =>
    matchesBrandSearch(item, search),
  );
  const safePage = Math.max(1, Number(page) || 1);
  const safePerPage = Math.max(1, Number(perPage) || BRANDS_PER_PAGE);
  const start = (safePage - 1) * safePerPage;
  const items = filtered.slice(start, start + safePerPage);
  const total = filtered.length;

  return {
    items,
    currentPage: safePage,
    lastPage: Math.max(1, Math.ceil(total / safePerPage) || 1),
    total,
    perPage: safePerPage,
  };
};

export const fetchBrandOptions = async () => {
  const items = await fetchBrandsCatalog();
  return items.map(toBrandOption);
};

export const fetchAllBrands = fetchBrandsCatalog;

export const filterBrandOptions = (options, { inputValue } = {}) => {
  const query = String(inputValue || "")
    .trim()
    .toLowerCase();
  const matches = query
    ? options.filter(
        (option) =>
          String(option?.name || "")
            .toLowerCase()
            .includes(query) ||
          String(option?.slug || "")
            .toLowerCase()
            .includes(query),
      )
    : options;
  return matches.slice(0, BRAND_OPTION_LIMIT);
};

export const useBrandSearch = (selectedBrand = null) => {
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        const items = await fetchBrandOptions();
        if (cancelled) return;
        setBrands(items);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching brands:", error);
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    };

    loadBrands();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedBrand?.id) return;
    setBrands((prev) => {
      if (prev.some((item) => String(item.id) === String(selectedBrand.id))) {
        return prev;
      }
      return [toBrandOption(selectedBrand), ...prev];
    });
  }, [selectedBrand]);

  return {
    brands,
    loadingBrands,
  };
};

export const normalizeBrandRow = (item, index) => ({
  index: index + 1,
  id: item.id,
  name: item.name || "",
  slug: item.slug || "",
  image_url: item.image_url || "",
});
